import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { Reveal } from "@/components/common/Reveal";
import { Eyebrow } from "@/components/common/Section";
import { EdmontonMap } from "@/components/relocating/EdmontonMap";
import { EDMONTON_COMMUNITIES } from "@/data/communities";

/**
 * Split explorer for the Terrain section on /relocating.
 * LEFT — eyebrow + filter chips + scrollable community list (38% width)
 * RIGHT — sticky Mapbox map (62% width)
 * Both columns are synced: hovering a list row highlights the marker
 * and vice-versa. Filter chips fade non-matching markers to 25% opacity
 * and animate list rows in/out with Framer Motion's layout system.
 */

// Category → slugs (per spec)
const FILTERS = [
  { key: "all", label: "All", slugs: null },
  {
    key: "families",
    label: "Families",
    slugs: [
      "mill-woods",
      "windermere",
      "chappelle",
      "glenora",
      "ottewell",
      "bonnie-doon",
    ],
  },
  {
    key: "working-downtown",
    label: "Working downtown",
    slugs: [
      "westmount",
      "glenora",
      "crestwood",
      "forest-heights",
      "old-strathcona",
    ],
  },
  {
    key: "near-nature",
    label: "Near nature",
    slugs: ["forest-heights", "crestwood", "glenora", "bonnie-doon"],
  },
  {
    key: "students-campus",
    label: "Students & campus",
    slugs: ["old-strathcona", "bonnie-doon"],
  },
  {
    key: "new-builds",
    label: "New builds",
    slugs: ["windermere", "chappelle"],
  },
  {
    key: "best-value",
    label: "Best value",
    slugs: ["mill-woods", "mayfield", "forest-heights"],
  },
  {
    key: "quiet-established",
    label: "Quiet & established",
    slugs: ["glenora", "crestwood", "ottewell", "westmount", "mayfield"],
  },
];

// Inverse index → for each community, which category labels does it belong to?
const TAGS_BY_SLUG = (() => {
  const map = {};
  EDMONTON_COMMUNITIES.forEach((c) => (map[c.slug] = []));
  FILTERS.forEach((f) => {
    if (!f.slugs) return;
    f.slugs.forEach((s) => {
      if (map[s]) map[s].push(f.label);
    });
  });
  return map;
})();

export const TerrainExplorer = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState("all");
  const [hoveredSlug, setHoveredSlug] = useState(null);
  const listWrapRef = useRef(null);
  const rowRefs = useRef({});

  /* Pre-select a filter from ?filter= — used when arriving from the
   * homepage lifestyle gallery. Accepts a few common aliases so short
   * URLs like "students" or "downtown" still hit the right chip. */
  useEffect(() => {
    const q = (searchParams.get("filter") || "").toLowerCase();
    if (!q) return;
    const alias = {
      students: "students-campus",
      campus: "students-campus",
      downtown: "working-downtown",
      "new-here": "all",
      scout: "all",
    };
    const key = alias[q] || q;
    if (FILTERS.some((f) => f.key === key)) setFilter(key);
  }, [searchParams]);

  const activeFilter = FILTERS.find((f) => f.key === filter) || FILTERS[0];
  const filteredSlugs = useMemo(
    () => (activeFilter.slugs ? new Set(activeFilter.slugs) : null),
    [activeFilter],
  );

  const rows = useMemo(() => {
    if (!filteredSlugs) return EDMONTON_COMMUNITIES;
    return EDMONTON_COMMUNITIES.filter((c) => filteredSlugs.has(c.slug));
  }, [filteredSlugs]);

  /* Scroll hovered row into view (only when hover comes from the map,
   * not the list itself — otherwise we'd wrestle with the pointer). */
  const lastHoverSourceRef = useRef("none");
  useEffect(() => {
    if (!hoveredSlug || lastHoverSourceRef.current !== "map") return;
    const el = rowRefs.current[hoveredSlug];
    const wrap = listWrapRef.current;
    if (!el || !wrap) return;
    const elRect = el.getBoundingClientRect();
    const wrapRect = wrap.getBoundingClientRect();
    if (elRect.top < wrapRect.top || elRect.bottom > wrapRect.bottom) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [hoveredSlug]);

  const handleMapHover = (slug) => {
    lastHoverSourceRef.current = "map";
    setHoveredSlug(slug);
  };
  const handleRowHover = (slug) => {
    lastHoverSourceRef.current = "list";
    setHoveredSlug(slug);
  };

  return (
    <section
      id="terrain"
      style={{ backgroundColor: "#EDE9E0" }}
      data-nav-theme="light"
      data-testid="relocating-terrain"
    >
      <div className="mx-auto max-w-[1440px] px-6 pb-24 pt-24 md:px-10 md:pb-32 md:pt-32">
        {/* Split area — desktop 38/62, mobile stacked */}
        <div className="grid grid-cols-1 gap-8 md:grid-cols-[38%_62%] md:gap-10 md:items-start md:min-h-[85vh]">
          {/* -------- LEFT PANEL -------- */}
          <div
            className="flex flex-col md:h-[85vh] md:sticky md:top-24"
            data-testid="terrain-panel"
          >
            <div>
              <Eyebrow>The terrain</Eyebrow>
              <h3
                className="font-display mt-4"
                style={{
                  fontSize: "clamp(30px,3.8vw,54px)",
                  color: "#201E1A",
                  lineHeight: 1.02,
                  letterSpacing: "-0.015em",
                }}
                data-testid="terrain-title"
              >
                Find your{" "}
                <span className="italic" style={{ color: "#C89A4E" }}>
                  fit.
                </span>
              </h3>

              {/* Filter chips */}
              <div
                className="mt-6 flex flex-wrap gap-2"
                data-testid="terrain-filters"
              >
                {FILTERS.map((f) => {
                  const on = filter === f.key;
                  return (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setFilter(f.key)}
                      aria-pressed={on}
                      data-testid={`filter-${f.key}`}
                      className="rounded-full px-3.5 py-1.5 text-[12px] transition-colors"
                      style={{
                        backgroundColor: on ? "#24382E" : "transparent",
                        color: on ? "#F8F5EF" : "#24382E",
                        border: `1px solid ${
                          on ? "#24382E" : "rgba(32,30,26,0.2)"
                        }`,
                      }}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Community list — scrolls within the panel */}
            <div
              ref={listWrapRef}
              className="mt-6 flex-1 overflow-y-auto pr-1"
              data-testid="terrain-list"
              style={{ scrollbarWidth: "thin" }}
            >
              <AnimatePresence mode="popLayout">
                {rows.map((c) => (
                  <motion.button
                    key={c.slug}
                    ref={(el) => (rowRefs.current[c.slug] = el)}
                    type="button"
                    layout
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                    onMouseEnter={() => handleRowHover(c.slug)}
                    onMouseLeave={() => handleRowHover(null)}
                    onFocus={() => handleRowHover(c.slug)}
                    onBlur={() => handleRowHover(null)}
                    onClick={() => navigate(`/neighbourhoods/${c.slug}`)}
                    data-testid={`terrain-row-${c.slug}`}
                    className="block w-full rounded-2xl px-4 py-4 text-left transition-colors"
                    style={{
                      backgroundColor:
                        hoveredSlug === c.slug ? "#F8F5EF" : "transparent",
                      border: `1px solid ${
                        hoveredSlug === c.slug
                          ? "rgba(32,30,26,0.14)"
                          : "transparent"
                      }`,
                    }}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <span
                        className="font-display"
                        style={{
                          fontSize: 18,
                          color: "#201E1A",
                          lineHeight: 1.15,
                        }}
                      >
                        {c.name}
                      </span>
                      <span
                        className="label-caps shrink-0 text-[10px]"
                        style={{ color: "#C89A4E" }}
                      >
                        {c.rent}
                      </span>
                    </div>
                    <p
                      className="mt-1.5 text-[13px] leading-[1.45]"
                      style={{ color: "rgba(32,30,26,0.7)" }}
                    >
                      {c.vibe}
                    </p>
                    {/* Category tags as tiny dot + label */}
                    <div className="mt-2.5 flex flex-wrap gap-x-3 gap-y-1">
                      {(TAGS_BY_SLUG[c.slug] || []).map((t) => (
                        <span
                          key={t}
                          className="inline-flex items-center gap-1.5 text-[10px]"
                          style={{ color: "rgba(32,30,26,0.55)" }}
                        >
                          <span
                            className="inline-block h-1.5 w-1.5 rounded-full"
                            style={{ backgroundColor: "#24382E" }}
                          />
                          {t}
                        </span>
                      ))}
                    </div>
                  </motion.button>
                ))}
              </AnimatePresence>
            </div>
          </div>

          {/* -------- RIGHT PANEL — sticky map -------- */}
          <div className="md:sticky md:top-24 md:h-[85vh]">
            <div className="h-[60vh] md:h-full">
              <EdmontonMap
                hoveredSlug={hoveredSlug}
                filteredSlugs={filteredSlugs}
                onHover={handleMapHover}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TerrainExplorer;
